import * as React from "react";
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  IconButton,
  LinearProgress,
  List,
  Toolbar,
  Typography,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { getPlayedScenarioIds } from "../../LocalStorage";
import { LOCATIONS } from "../../Constants";
import {
  CUSTOM_SCENARIO_ID,
  DEFAULT_CUSTOM_SCENARIO,
  SCENARIOS,
  TUTORIALS,
} from "../../data/Scenarios";
import { GameType, ScenarioType } from "../../Types";

export interface StateProps {
  game: GameType;
}

export interface DispatchProps {
  onBack: () => void;
  onCustomGame: () => void;
  onDetails: (delta: Partial<GameType>) => void;
  onManual: () => void;
  onTutorial: (scenarioId: number) => void;
}

export interface Props extends StateProps, DispatchProps {}

interface TutorialListItemProps {
  completed: boolean;
  // The first tutorial the player hasn't done yet, called out so the sequence has an
  // obvious entry point instead of six equal-looking rows
  next: boolean;
  s: ScenarioType;
  onTutorial: DispatchProps["onTutorial"];
}

function TutorialListItem(props: TutorialListItemProps): React.JSX.Element {
  const { s, onTutorial, completed, next } = props;
  return (
    <Card className={`build-list-item${next ? " tutorialNext" : ""}`}>
      <CardHeader
        style={{ opacity: completed ? 0.6 : 1 }}
        avatar={
          completed ? (
            <CheckCircleIcon
              className="tutorialComplete"
              color="primary"
              titleAccess={`${s.name} completed`}
            />
          ) : (
            <RadioButtonUncheckedIcon
              className="tutorialIncomplete"
              titleAccess={`${s.name} not yet completed`}
            />
          )
        }
        action={
          <Button
            size="small"
            variant={completed ? "outlined" : "contained"}
            color="primary"
            onClick={() => onTutorial(s.id)}
            autoFocus={next}
          >
            {completed ? "Replay" : "Play"}
          </Button>
        }
        title={s.name}
        subheader={next ? "Start here" : undefined}
      />
    </Card>
  );
}

interface ScenarioListItemProps {
  s: ScenarioType;
  // The custom game row opens its own setup screen rather than the scenario details one, so the
  // row doesn't get to assume what selecting it does
  onSelect: () => void;
}

function ScenarioListItem(props: ScenarioListItemProps): React.JSX.Element {
  const { s, onSelect } = props;
  const location = LOCATIONS[s.locationId] || {
    name: "UNKNOWN",
  };
  const summary =
    s.id === CUSTOM_SCENARIO_ID ? (
      s.summary
    ) : (
      <span>
        {s.summary}
        <br />
        <i>
          {location.name}, {s.startingYear}-
          {s.startingYear + s.durationMonths / 12}
        </i>
      </span>
    );
  return (
    <Card className="build-list-item clickable-card" onClick={onSelect}>
      <CardHeader
        avatar={<Avatar src={`/images/${s.icon.toLowerCase()}.svg`} />}
        title={s.name}
        subheader={summary}
        action={
          <IconButton color="primary" onClick={onSelect} size="large">
            <ArrowRightIcon />
          </IconButton>
        }
      />
    </Card>
  );
}

export default function NewGame(props: Props): React.JSX.Element {
  const ids = getPlayedScenarioIds();
  const completedTutorials = TUTORIALS.filter(
    (s) => ids.indexOf(s.id) !== -1,
  ).length;
  const nextTutorial = TUTORIALS.find((s) => ids.indexOf(s.id) === -1);

  return (
    <div id="listCard" className="flexContainer">
      <div id="topbar">
        <Toolbar>
          <IconButton
            onClick={props.onBack}
            aria-label="back"
            edge="start"
            color="primary"
            size="large"
          >
            <ArrowBackIosIcon />
          </IconButton>
          <Typography variant="h6">Select a Scenario</Typography>
          {/* Otherwise the Manual is only reachable from the title screen and the in-game
              overflow menu, so players who stop partway through never find out it exists.
              Auto margin rather than absolute positioning, so it can't sit on top of the
              title on narrow screens */}
          <IconButton
            sx={{ marginLeft: "auto" }}
            onClick={props.onManual}
            aria-label="manual"
            color="primary"
            size="large"
          >
            <HelpOutlineIcon />
          </IconButton>
        </Toolbar>
      </div>
      <List dense className="scrollable cardList">
        <Typography variant="h5" sx={{ paddingLeft: 1, paddingTop: 1 }}>
          Tutorials
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ paddingLeft: 1, paddingBottom: 1 }}
        >
          {completedTutorials} of {TUTORIALS.length} complete
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(completedTutorials / TUTORIALS.length) * 100}
          className="tutorialProgressBar"
          aria-label={`Tutorials: ${completedTutorials} of ${TUTORIALS.length} complete`}
        />
        {TUTORIALS.map((s) => {
          return (
            <TutorialListItem
              key={s.id}
              onTutorial={props.onTutorial}
              s={s}
              completed={ids.indexOf(s.id) !== -1}
              next={nextTutorial !== undefined && s.id === nextTutorial.id}
            />
          );
        })}
        <Typography variant="h5" sx={{ paddingLeft: 1, paddingTop: 1 }}>
          Scenarios
        </Typography>
        {SCENARIOS.filter((s: ScenarioType) => !s.tutorialSteps).map((s) => {
          return (
            <ScenarioListItem
              key={s.id}
              onSelect={() => props.onDetails({ scenarioId: s.id })}
              s={s}
            />
          );
        })}
        <ScenarioListItem
          key={CUSTOM_SCENARIO_ID}
          onSelect={props.onCustomGame}
          s={DEFAULT_CUSTOM_SCENARIO}
        />
      </List>
    </div>
  );
}
